import { useCallback, useEffect, useState, type FormEvent } from 'react'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { useOrgStore } from '../../shared/store/org.store.js'
import { getCustomers, type CustomerListItem, type CustomerListMeta } from '../../shared/api/customers.js'
import { canViewCustomers } from '../../shared/utils/permissions.js'
import { ApiError } from '../../shared/types/api.js'
import { ROUTES } from '../../app/routes.js'
import OrderStatusBadge from '../orders/components/OrderStatusBadge.js'
import EmptyState from '../menu/components/EmptyState.js'
import { SkeletonOrderRow } from '../../shared/ui/Skeleton.js'

type ListStatus = 'loading' | 'ready' | 'error' | 'forbidden'

const PAGE_SIZE = 20

function formatAmount(value: number, currency: string): string {
  return `${value.toLocaleString('ru-RU', { maximumFractionDigits: 2 })} ${currency}`
}

export default function CustomersPage() {
  const { t } = useTranslation()
  const navigate = useNavigate()

  const org = useOrgStore(s => s.activeOrganization)
  const orgId = org?.id
  const canView = canViewCustomers(org?.role)

  const [customers, setCustomers] = useState<CustomerListItem[]>([])
  const [meta, setMeta] = useState<CustomerListMeta>({ total: 0, page: 1, pageSize: PAGE_SIZE, totalPages: 1 })
  const [searchInput, setSearchInput] = useState('')
  const [search, setSearch] = useState('')
  const [page, setPage] = useState(1)
  const [status, setStatus] = useState<ListStatus>('loading')

  const load = useCallback(async () => {
    if (!orgId) return
    setStatus('loading')
    try {
      const res = await getCustomers(orgId, {
        page,
        pageSize: PAGE_SIZE,
        search: search || undefined,
      })
      setCustomers(res.data)
      setMeta(res.meta)
      setStatus('ready')
    } catch (err: unknown) {
      setStatus(err instanceof ApiError && err.status === 403 ? 'forbidden' : 'error')
    }
  }, [orgId, page, search])

  useEffect(() => {
    if (!canView) {
      setStatus('forbidden')
      return
    }
    void load()
  }, [load, canView])

  function submitSearch(e: FormEvent) {
    e.preventDefault()
    setPage(1)
    setSearch(searchInput.trim())
  }

  if (!orgId) return null

  if (!canView || status === 'forbidden') {
    return (
      <section className="page-section">
        <h2>{t('app.customers')}</h2>
        <div className="settings-card">
          <div className="form-error-banner">{t('customers.forbidden')}</div>
        </div>
      </section>
    )
  }

  if (status === 'loading' && customers.length === 0) {
    return (
      <section className="page-section">
        <h2>{t('app.customers')}</h2>
        <div className="page-loading">
          {Array.from({ length: 5 }, (_, i) => <SkeletonOrderRow key={i} />)}
        </div>
      </section>
    )
  }

  if (status === 'error') {
    return (
      <section className="page-section">
        <h2>{t('app.customers')}</h2>
        <div className="settings-card">
          <div className="form-error-banner">{t('customers.loadError')}</div>
          <button type="button" className="btn btn-primary" onClick={() => void load()}>
            {t('error.retry')}
          </button>
        </div>
      </section>
    )
  }

  return (
    <section className="page-section page-section--wide">
      <div className="page-header">
        <div>
          <h2>{t('app.customers')}</h2>
          <p className="page-section-description">{t('customers.listSubtitle')}</p>
        </div>
      </div>

      <form className="product-filters" onSubmit={submitSearch}>
        <div className="form-field">
          <label htmlFor="customerSearch">{t('customers.searchPlaceholder')}</label>
          <input
            id="customerSearch"
            type="search"
            value={searchInput}
            placeholder={t('customers.searchPlaceholder')}
            onChange={e => setSearchInput(e.target.value)}
          />
        </div>
        <div className="form-field">
          <button type="submit" className="btn btn-primary">{t('customers.search')}</button>
        </div>
      </form>

      {customers.length === 0 ? (
        search ? (
          <EmptyState
            title={t('customers.noResults')}
            hint={t('customers.noResultsHint')}
          />
        ) : (
          <EmptyState title={t('customers.empty')} hint={t('customers.emptyHint')} />
        )
      ) : (
        <>
          <ul className="order-list">
            {customers.map(customer => (
              <li
                key={customer.customerKey}
                className="order-row"
                onClick={() => navigate(ROUTES.CUSTOMERS_DETAIL.replace(':customerKey', customer.customerKey))}
                role="button"
                tabIndex={0}
                onKeyDown={e => {
                  if (e.key === 'Enter') {
                    navigate(ROUTES.CUSTOMERS_DETAIL.replace(':customerKey', customer.customerKey))
                  }
                }}
              >
                <div className="order-row-main">
                  <span className="order-number">{customer.name || '—'}</span>
                  <span className="order-row-type">{customer.phone || '—'}</span>
                </div>
                <span className="order-row-customer">{customer.email || '—'}</span>
                <span className="order-row-status">{t('customers.ordersCount', { count: customer.ordersCount })}</span>
                <div className="order-row-total">{formatAmount(customer.totalSpent, customer.currency)}</div>
                <div className="order-row-created">{new Date(customer.lastOrderAt).toLocaleString()}</div>
                <OrderStatusBadge status={customer.lastStatus} />
              </li>
            ))}
          </ul>

          {meta.totalPages > 1 && (
            <div className="pagination">
              <button
                type="button"
                className="btn btn-ghost"
                disabled={meta.page <= 1}
                onClick={() => setPage(p => Math.max(1, p - 1))}
              >
                {t('customers.previous')}
              </button>
              <span className="pagination-label">{t('customers.pageOf', { page: meta.page, total: meta.totalPages })}</span>
              <button
                type="button"
                className="btn btn-ghost"
                disabled={meta.page >= meta.totalPages}
                onClick={() => setPage(p => p + 1)}
              >
                {t('customers.next')}
              </button>
            </div>
          )}
        </>
      )}
    </section>
  )
}
